import React, { useEffect, useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import { FiltersButton } from '../../components';
import * as chickenDashActions from '../../redux/actions/chickenDashAction';
import { DashboardSettingsModal } from './DashboardSettingsModal';
import { DashboardFiltersModal } from './DashboardFiltersModal';
import { changeRestaurantWithTimezone } from '../../utils/commonMethods';
import { setSearchParameters } from '../../utils/urlFunctions';
import { useLazyGetFriedMaterials, useLazyGetGrilledMaterials } from '../../api/dictionary';
import useIsMobile from '../../hooks/useIsMobile';
import useChickenDashboardDistReqParams from '../../hooks/chickenDashboard/common/useChickenDashboardDistReqParams';
import ExportButton from './Export/ExportButton';
import DashboardRoutes from './DashboardRoutes';
import DashboardNavBar from './DashboardNavBar';
import DashboardToolbar from './Toolbar/DashboardToolbar';
import ChickenDashboardContextProvider from './ChickenDashboardContextProvider';

function DashboardLayout({ setCurrentPage }) {
  const history = useHistory();
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  const distReqParams = useChickenDashboardDistReqParams();
  
  const [getFriedMaterials] = useLazyGetFriedMaterials();
  const [getGrilledMaterials] = useLazyGetGrilledMaterials();
  
  const [materials, setMaterials] = useState([]);
  const [changedSearch, setChangedSearch] = useState(false);
  const [filtersModal, setFiltersModal] = useState(false);
  const [settingsModal, setSettingsModal] = useState(false);
  
  const chickenDashboard = useSelector(state => state.chickenDashboard);
  const login = useSelector(state => state.login);
  const app = useSelector(state => state.app);
  
  const search = useLocation().search;
  const cookingMethod = chickenDashboard.cookingMethod;
  
  const changeValue = useCallback((key, value) => {
    dispatch(chickenDashActions.changeValue(key, value));
  }, [dispatch]);
  
  const changeSeveralValues = useCallback(value => {
    dispatch(chickenDashActions.changeSeveralValues(value));
  }, [dispatch]);
  
  const getMaterials = useCallback(() => {
    let fetchMaterials;
    
    if (cookingMethod === 'FRIED') {
      fetchMaterials = getFriedMaterials;
    } else if (cookingMethod === 'GRILLED') {
      fetchMaterials = getGrilledMaterials;
    }
    
    if (fetchMaterials) {
      fetchMaterials()
        .then(({ data: allMaterials }) => {
          const cookableMaterials = allMaterials.filter(material => material.tags.includes('COOKABLE'));
          const newChickenTypes = cookableMaterials.map(it => it.name).sort((a, b) => a.localeCompare(b));
          
          const newMaterials = cookableMaterials.map(item => {
            return {
              label: item.name,
              value: item.name,
              color: item.color,
              tags: item.tags,
            };
          });
          
          changeValue('chickenTypes', newChickenTypes);
          setMaterials(newMaterials);
        })
        .catch(() => {});
    }
  }, [getFriedMaterials, getGrilledMaterials, changeValue, cookingMethod]);
  
  const changeRestaurantValue = (value = null) => {
    changeRestaurantWithTimezone(value, history, login, chickenDashboard, changeValue, changeSeveralValues);
  };
  
  useEffect(() => {
    setCurrentPage('Dashboard');
    
    if (login.restaurantUuid !== 'select') {
      changeValue('restaurantUuid', login.restaurantUuid);
    }
  }, [setCurrentPage, login.restaurantUuid, changeValue]);
  
  useEffect(() => {
    changeValue('chickenTypes', []);
    setMaterials([]);
    getMaterials();
  }, [getMaterials, changeValue]);
  
  useEffect(() => {
    setChangedSearch(true);
  }, [search]);
  
  useEffect(() => {
    if (filtersModal && app.screen !== 'mobile') {
      setFiltersModal(false);
    }
  }, [app.screen]);
  
  useEffect(() => {
    login.restaurantUuid !== 'select'
      ? changeRestaurantValue(login.restaurantUuid)
      : login.restaurantUuid === 'select' &&
      chickenDashboard.restaurantUuid !== 'select' &&
      changeRestaurantValue('select');
  }, [login.restaurantUuid]);
  
  useEffect(() => {
    changeRestaurantValue(chickenDashboard.restaurantUuid);
  }, [login.restaurantOptions, login.timezone]);
  
  useEffect(() => {
    return () => {
      dispatch(chickenDashActions.resetStore());
    };
  }, [dispatch]);
  
  const confirmModal = data => {
    changeSeveralValues({ ...data });
    history.push(
      setSearchParameters(
        ['restaurantUuid', 'startDate', 'endDate', 'startTimeInterval', 'endTimeInterval'],
        [
          data.restaurantUuid !== 'select' ? data.restaurantUuid : null,
          data.startDate !== 'select' ? data.startDate : null,
          data.endDate !== 'select' ? data.endDate : null,
          data.startTimeInterval ? data.startTimeInterval : null,
          data.endTimeInterval ? data.endTimeInterval : null,
        ]
      )
    );
    setFiltersModal(false);
  };

  const confirmSettings = () => {
    setSettingsModal(false);
  };
  
  return (
    <div className="page-container">
      <div className="page-header-container">
        <div className="page-header">
          <h1 className="page-title">Dashboard</h1>
          
          <div className="page-header__tools">
            <ExportButton distReqParams={distReqParams} />
            
            {isMobile ? (
              <FiltersButton
                disabled={chickenDashboard.isLoading}
                activeFilters={
                  (login.restaurantUuid === 'select' &&
                  chickenDashboard.restaurantUuid !== 'select' &&
                  chickenDashboard.restaurantUuid !== null
                    ? 1
                    : 0) +
                  ((chickenDashboard.startDate !== null || chickenDashboard.timeInterval !== null) &&
                  chickenDashboard.startDate !== 'select'
                    ? 1
                    : 0) +
                  (chickenDashboard.period !== 'DAY' ? 1 : 0) +
                  (chickenDashboard.chickenTypes.length !== materials.length ? 1 : 0)
                }
                onClick={() => {
                  setFiltersModal(true);
                }}
              />
            ) : (
              <FiltersButton
                disabled={chickenDashboard.isLoading}
                activeFilters={0}
                onClick={() => {
                  setSettingsModal(true);
                }}
              />
            )}
            
            {!isMobile ? <DashboardNavBar /> : null}
          </div>
        </div>
        
        {isMobile ? (
          <div className="page-subheader">
            <DashboardNavBar />
          </div>
        ) : null}
        
        <DashboardToolbar
          changedSearch={changedSearch}
          setChangedSearch={setChangedSearch}
          materials={materials}
        />
      </div>
  
      <ChickenDashboardContextProvider distReqParams={distReqParams}>
        <DashboardRoutes setChangedSearch={setChangedSearch} materials={materials} />
      </ChickenDashboardContextProvider>
      
      {filtersModal ? (
        <DashboardFiltersModal
          login={login}
          info={chickenDashboard}
          chickenTypesOptions={materials}
          isTimeRangeEnabled
          onCancel={() => setFiltersModal(false)}
          onConfirm={data => confirmModal(data)}
        />
      ) : null}
      
      {settingsModal ? (
        <DashboardSettingsModal onConfirm={confirmSettings} onCancel={() => setSettingsModal(false)} />
      ) : null}
    </div>
  );
}

export default DashboardLayout;

